import { useState, useEffect } from 'react';
import { 
  Calendar,
  Clock,
  MapPin,
  Phone,
  Mail,
  User,
  Plus,
  Edit,
  Eye,
  Trash2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Search,
  Filter,
  FileText,
  MessageCircle,
  ChevronLeft,
  ChevronRight,
  List,
  Grid3X3,
  Send,
  Download,
  ExternalLink,
  Users,
  Building2
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Separator } from './ui/separator';
import { Checkbox } from './ui/checkbox';
import { Appointment } from '../types/appointment';
import { TransferSigning } from '../types/transfer-signing';
import { toast } from 'sonner';
import { logger } from '../services/logger.service';
import { appointmentAPI } from '../lib/appointment-api';
import { getClients } from '../lib/client-api';
import { Client } from '../types/client';

const timeSlots: string[] = [
  '08:00','08:30','09:00','09:30','10:00','10:30','11:00','11:30',
  '12:00','12:30','13:00','13:30','14:00','14:30','15:00','15:30',
  '16:00','16:30','17:00','17:30','18:00',
];
const appointmentTypes: string[] = [
  'Initial Consultation', 'Property Viewing', 'Contract Signing', 'Final Walkthrough', 'Transfer Documents',
];
const locationTypes: string[] = ['Office', 'Property', 'Client Location', 'Virtual'];
const transferSigningTimeSlots: string[] = [];
const commonConveyancers: string[] = [];
export function Appointments() {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newAppointment, setNewAppointment] = useState({
    clientId: '',
    propertyId: '',
    type: '',
    date: '',
    time: '',
    duration: 60,
    location: '',
    locationDetails: '',
    notes: '',
    reminders: {
      email: true,
      sms: true,
      whatsapp: false
    }
  });

  useEffect(() => {
    loadAppointments();
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      const fetched = await getClients();
      setClients(fetched);
    } catch (error) {
      logger.error('Failed to load clients', error as Error, undefined, 'Appointments');
    }
  };

  const loadAppointments = async () => {
    try {
      setIsLoading(true);
      const fetchedAppointments = await appointmentAPI.getAppointments();
      
      setAppointments(fetchedAppointments);
    } catch (error) {
      logger.error('Failed to load appointments', error as Error, undefined, 'Appointments');
      toast.error('Failed to load appointments');
      setAppointments([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Confirmed': return 'bg-green-100 text-green-800';
      case 'Scheduled': return 'bg-blue-100 text-blue-800';
      case 'Completed': return 'bg-gray-100 text-gray-800';
      case 'Cancelled': return 'bg-red-100 text-red-800';
      case 'No Show': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Confirmed': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'Scheduled': return <Clock className="w-4 h-4 text-blue-500" />;
      case 'Completed': return <CheckCircle className="w-4 h-4 text-gray-500" />;
      case 'Cancelled': return <XCircle className="w-4 h-4 text-red-500" />;
      case 'No Show': return <AlertTriangle className="w-4 h-4 text-orange-500" />;
      default: return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getLocationIcon = (location: string) => {
    switch (location) {
      case 'Office': return <MapPin className="w-4 h-4 text-blue-500" />;
      case 'Property': return <MapPin className="w-4 h-4 text-green-500" />;
      case 'Client Location': return <MapPin className="w-4 h-4 text-orange-500" />;
      case 'Virtual': return <MessageCircle className="w-4 h-4 text-purple-500" />;
      default: return <MapPin className="w-4 h-4 text-gray-500" />;
    }
  };

  const filteredAppointments = appointments.filter(appointment => {
    const matchesSearch = (appointment.clientName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (appointment.type || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (appointment.propertyAddress && appointment.propertyAddress.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus = statusFilter === 'all' || (appointment.status || '').toLowerCase() === statusFilter.toLowerCase();
    const matchesType = typeFilter === 'all' || (appointment.type || '').toLowerCase() === typeFilter.toLowerCase();

    return matchesSearch && matchesStatus && matchesType;
  });

  const upcomingAppointments = filteredAppointments
    .filter(apt => {
      const s = (apt.status || '').toLowerCase();
      return s === 'scheduled' || s === 'confirmed';
    })
    .sort((a, b) => new Date(`${a.date} ${a.time}`).getTime() - new Date(`${b.date} ${b.time}`).getTime());

  const handleCreateAppointment = () => {
    toast.success('Appointment scheduled successfully');
    setIsCreateOpen(false);
    setNewAppointment({
      clientId: '',
      propertyId: '',
      type: '',
      date: '',
      time: '',
      duration: 60,
      location: '',
      locationDetails: '',
      notes: '',
      reminders: {
        email: true,
        sms: true,
        whatsapp: false
      }
    });
  };

  const handleUpdateStatus = (appointmentId: string, status: string) => {
    toast.success(`Appointment ${status.toLowerCase()}`);
  };

  const handleSendReminder = (appointmentId: string) => {
    toast.success('Reminder sent to client');
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-ZA', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (timeStr: string) => {
    return new Date(`2000-01-01 ${timeStr}`).toLocaleTimeString('en-ZA', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  // Calendar helper functions
  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const formatCalendarDate = (date: Date) => {
    return date.toISOString().split('T')[0];
  };

  const getAppointmentsForDate = (dateStr: string) => {
    return appointments.filter(apt => apt.date === dateStr);
  };

  const isToday = (dateStr: string) => {
    return dateStr === formatCalendarDate(new Date());
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prevDate => {
      const newDate = new Date(prevDate);
      if (direction === 'prev') {
        newDate.setMonth(newDate.getMonth() - 1);
      } else {
        newDate.setMonth(newDate.getMonth() + 1);
      }
      return newDate;
    });
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const handleDateClick = (dateStr: string) => {
    setSelectedDate(dateStr);
    setNewAppointment(prev => ({ ...prev, date: dateStr }));
    setIsCreateOpen(true);
  };

  const getAgentColor = (agentId: string) => {
    const colors = [
      'bg-blue-500',
      'bg-green-500', 
      'bg-purple-500',
      'bg-orange-500',
      'bg-pink-500',
      'bg-cyan-500'
    ];
    return colors[parseInt(agentId) % colors.length];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-6">
          <div>
            <h3>Appointments & Transfer Signings</h3>
            <p className="text-muted-foreground">
              Schedule and manage client appointments and property transfer signings
            </p>
          </div>
          
          {/* View Toggle */}
          <div className="flex items-center space-x-1 border rounded-lg p-1">
            <Button
              data-testid="appointments-list-view-button"
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className="px-3"
            >
              <List className="w-4 h-4 mr-2" />
              List
            </Button>
            <Button
              data-testid="appointments-calendar-view-button"
              variant={viewMode === 'calendar' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('calendar')}
              className="px-3"
            >
              <Grid3X3 className="w-4 h-4 mr-2" />
              Calendar
            </Button>
          </div>
        </div>
        
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="appointments-schedule-button">
              <Plus className="w-4 h-4 mr-2" />
              Schedule Appointment
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Schedule New Appointment</DialogTitle>
              <DialogDescription>
                Create a new appointment for client meetings, document signings, or property viewings
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-6 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Client</Label>
                  <Select value={newAppointment.clientId} onValueChange={(value) => {
                    setNewAppointment(prev => ({ ...prev, clientId: value }));
                    setClientSearchTerm('');
                  }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select client" />
                    </SelectTrigger>
                    <SelectContent>
                      <div className="p-2 sticky top-0 bg-background border-b">
                        <div className="relative">
                          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                          <Input
                            placeholder="Search clients..."
                            value={clientSearchTerm}
                            onChange={(e) => setClientSearchTerm(e.target.value)}
                            className="pl-8 h-8"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      </div>
                      <div className="max-h-[200px] overflow-y-auto">
                        {clients
                          .filter(client =>
                            (client.name || '').toLowerCase().includes(clientSearchTerm.toLowerCase()) ||
                            (client.email || '').toLowerCase().includes(clientSearchTerm.toLowerCase()) ||
                            (client.phone || '').includes(clientSearchTerm)
                          )
                          .map((client) => (
                            <SelectItem key={client.id} value={client.id}>
                              {client.name} - {client.email}
                            </SelectItem>
                          ))}
                        {clients.filter(client =>
                          client.name.toLowerCase().includes(clientSearchTerm.toLowerCase()) ||
                          client.email.toLowerCase().includes(clientSearchTerm.toLowerCase()) ||
                          client.phone.includes(clientSearchTerm)
                        ).length === 0 && (
                          <div className="p-2 text-sm text-muted-foreground text-center">
                            No clients found
                          </div>
                        )}
                      </div>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Appointment Type</Label>
                  <Select value={newAppointment.type} onValueChange={(value) => 
                    setNewAppointment(prev => ({ ...prev, type: value }))
                  }>
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {appointmentTypes.map((type) => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Property (Optional)</Label>
                <Select value={newAppointment.propertyId} onValueChange={(value) => 
                  setNewAppointment(prev => ({ ...prev, propertyId: value }))
                }>
                  <SelectTrigger>
                    <SelectValue placeholder="Select property" />
                  </SelectTrigger>
                  <SelectContent>
                    {[].map((property) => (
                      <SelectItem key={property.id} value={property.id}>
                        {property.title} - {property.address.suburb}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input 
                    type="date" 
                    value={newAppointment.date}
                    onChange={(e) => setNewAppointment(prev => ({ ...prev, date: e.target.value }))}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Time</Label>
                  <Select value={newAppointment.time} onValueChange={(value) => 
                    setNewAppointment(prev => ({ ...prev, time: value }))
                  }>
                    <SelectTrigger>
                      <SelectValue placeholder="Select time" />
                    </SelectTrigger>
                    <SelectContent>
                      {timeSlots.map((time) => (
                        <SelectItem key={time} value={time}>{time}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Duration (minutes)</Label>
                  <Select value={newAppointment.duration.toString()} onValueChange={(value) => 
                    setNewAppointment(prev => ({ ...prev, duration: parseInt(value) }))
                  }>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30">30 minutes</SelectItem>
                      <SelectItem value="45">45 minutes</SelectItem>
                      <SelectItem value="60">1 hour</SelectItem>
                      <SelectItem value="90">1.5 hours</SelectItem>
                      <SelectItem value="120">2 hours</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Location</Label>
                  <Select value={newAppointment.location} onValueChange={(value) => 
                    setNewAppointment(prev => ({ ...prev, location: value }))
                  }>
                    <SelectTrigger>
                      <SelectValue placeholder="Select location" />
                    </SelectTrigger>
                    <SelectContent>
                      {locationTypes.map((location) => (
                        <SelectItem key={location} value={location}>{location}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Location Details</Label>
                  <Input 
                    placeholder="Conference room, address, etc."
                    value={newAppointment.locationDetails}
                    onChange={(e) => setNewAppointment(prev => ({ ...prev, locationDetails: e.target.value }))}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea 
                  placeholder="Any additional notes or requirements..."
                  value={newAppointment.notes}
                  onChange={(e) => setNewAppointment(prev => ({ ...prev, notes: e.target.value }))}
                />
              </div>

              <div className="space-y-3">
                <Label>Reminders</Label>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="email-reminder"
                      checked={newAppointment.reminders.email}
                      onCheckedChange={(checked) => 
                        setNewAppointment(prev => ({ 
                          ...prev, 
                          reminders: { ...prev.reminders, email: checked as boolean }
                        }))
                      }
                    />
                    <Label htmlFor="email-reminder">Email reminder</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="sms-reminder"
                      checked={newAppointment.reminders.sms}
                      onCheckedChange={(checked) => 
                        setNewAppointment(prev => ({ 
                          ...prev, 
                          reminders: { ...prev.reminders, sms: checked as boolean }
                        }))
                      }
                    />
                    <Label htmlFor="sms-reminder">SMS reminder</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="whatsapp-reminder"
                      checked={newAppointment.reminders.whatsapp}
                      onCheckedChange={(checked) => 
                        setNewAppointment(prev => ({ 
                          ...prev, 
                          reminders: { ...prev.reminders, whatsapp: checked as boolean }
                        }))
                      }
                    />
                    <Label htmlFor="whatsapp-reminder">WhatsApp reminder</Label>
                  </div>
                </div>
              </div>

              <Button data-testid="appointments-create-submit-button" onClick={handleCreateAppointment} className="w-full">
                Schedule Appointment
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Appointments</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{appointments.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Upcoming</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{appointments.filter(a => a.status === 'Scheduled' || a.status === 'Confirmed').length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">This Week</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{appointments.filter(a => {
              const apt = new Date(`${a.date} ${a.time}`);
              const now = new Date();
              const weekEnd = new Date(now); weekEnd.setDate(now.getDate() + 7);
              return apt >= now && apt <= weekEnd;
            }).length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Completed</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{appointments.filter(a => a.status === 'Completed').length}</div>
          </CardContent>
        </Card>
      </div>

      {/* Upcoming Appointments */}
      {upcomingAppointments.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Upcoming Appointments</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {upcomingAppointments.slice(0, 3).map((appointment) => (
                <div key={appointment.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center space-x-4">
                    {getStatusIcon(appointment.status)}
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="font-medium">{appointment.clientName}</span>
                        <Badge variant="outline">{appointment.type}</Badge>
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-muted-foreground mt-1">
                        <span>{formatDate(appointment.date)} at {formatTime(appointment.time)}</span>
                        <span>•</span>
                        <div className="flex items-center space-x-1">
                          {getLocationIcon(appointment.location)}
                          <span>{appointment.location}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      data-testid="appointments-send-reminder-button"
                      size="sm"
                      variant="outline"
                      onClick={() => handleSendReminder(appointment.id)}
                    >
                      Send Reminder
                    </Button>
                    <Button
                      data-testid="appointments-view-details-button"
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setSelectedAppointment(appointment);
                        setIsDetailOpen(true);
                      }}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center space-x-2">
                <Calendar className="w-5 h-5" />
                <span>
                  {currentDate.toLocaleDateString('en-ZA', { 
                    month: 'long', 
                    year: 'numeric' 
                  })}
                </span>
              </CardTitle>
              <div className="flex items-center space-x-2">
                <Button data-testid="appointments-calendar-today-button" variant="outline" size="sm" onClick={goToToday}>
                  Today
                </Button>
                <Button data-testid="appointments-calendar-prev-button" variant="outline" size="sm" onClick={() => navigateMonth('prev')}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button data-testid="appointments-calendar-next-button" variant="outline" size="sm" onClick={() => navigateMonth('next')}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-7 gap-1 mb-4">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <div key={day} className="p-2 text-center text-sm font-medium text-muted-foreground">
                  {day}
                </div>
              ))}
            </div>
            
            <div className="grid grid-cols-7 gap-1">
              {/* Empty cells for days before month starts */}
              {Array.from({ length: getFirstDayOfMonth(currentDate) }).map((_, index) => (
                <div key={`empty-${index}`} className="p-2 h-24"></div>
              ))}
              
              {/* Calendar days */}
              {Array.from({ length: getDaysInMonth(currentDate) }).map((_, index) => {
                const day = index + 1;
                const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
                const dateStr = formatCalendarDate(date);
                const appointments = getAppointmentsForDate(dateStr);
                const isCurrentDay = isToday(dateStr);
                
                return (
                  <div
                    key={day}
                    className={`p-2 h-24 border rounded-lg cursor-pointer transition-colors hover:bg-muted/50 ${
                      isCurrentDay ? 'bg-primary/10 border-primary' : 'border-border'
                    }`}
                    onClick={() => handleDateClick(dateStr)}
                  >
                    <div className={`text-sm font-medium mb-1 ${isCurrentDay ? 'text-primary' : ''}`}>
                      {day}
                    </div>
                    <div className="space-y-1">
                      {appointments.slice(0, 3).map((apt, aptIndex) => (
                        <div
                          key={apt.id}
                          className={`text-xs p-1 rounded text-white truncate ${getAgentColor(apt.agentId || '1')}`}
                          title={`${apt.clientName} - ${apt.type} at ${formatTime(apt.time)}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedAppointment(apt);
                            setIsDetailOpen(true);
                          }}
                        >
                          {formatTime(apt.time)} {apt.clientName}
                        </div>
                      ))}
                      {appointments.length > 3 && (
                        <div className="text-xs text-muted-foreground">
                          +{appointments.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Calendar Legend */}
            <div className="mt-4 pt-4 border-t">
              <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                <span>Agent Colors:</span>
                {Array.from({ length: 4 }).map((_, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <div className={`w-3 h-3 rounded ${getAgentColor((index + 1).toString())}`}></div>
                    <span>Agent {index + 1}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search and Filters */}
      {viewMode === 'list' && (
        <div className="flex items-center space-x-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              placeholder="Search appointments by client, type, or property..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="Scheduled">Scheduled</SelectItem>
            <SelectItem value="Confirmed">Confirmed</SelectItem>
            <SelectItem value="Completed">Completed</SelectItem>
            <SelectItem value="Cancelled">Cancelled</SelectItem>
            <SelectItem value="No Show">No Show</SelectItem>
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {appointmentTypes.map((type) => (
              <SelectItem key={type} value={type}>{type}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        </div>
      )}

      {/* Appointments Table */}
      {viewMode === 'list' && (
        <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Client</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Date & Time</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredAppointments.map((appointment) => (
              <TableRow key={appointment.id}>
                <TableCell>
                  <div>
                    <p className="font-medium">{appointment.clientName}</p>
                    <p className="text-sm text-muted-foreground">{appointment.clientEmail}</p>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="outline">{appointment.type}</Badge>
                </TableCell>
                <TableCell>
                  <div>
                    <p>{formatDate(appointment.date)}</p>
                    <p className="text-sm text-muted-foreground">{formatTime(appointment.time)} ({appointment.duration}min)</p>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center space-x-2">
                    {getLocationIcon(appointment.location)}
                    <div>
                      <p className="text-sm">{appointment.location}</p>
                      {appointment.locationDetails && (
                        <p className="text-xs text-muted-foreground">{appointment.locationDetails}</p>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge className={getStatusColor(appointment.status)}>
                    {appointment.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center space-x-2">
                    <Button
                      data-testid="appointments-table-view-button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedAppointment(appointment);
                        setIsDetailOpen(true);
                      }}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button data-testid="appointments-table-edit-button" variant="ghost" size="sm">
                      <Edit className="w-4 h-4" />
                    </Button>
                    {(appointment.status === 'Scheduled' || appointment.status === 'Confirmed') && (
                      <Button
                        data-testid="appointments-table-complete-button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleUpdateStatus(appointment.id, 'Completed')}
                      >
                        <CheckCircle className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </Card>
      )}

      {/* Appointment Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-2xl">
          {selectedAppointment && (
            <>
              <DialogHeader>
                <DialogTitle>{selectedAppointment.type} - {selectedAppointment.clientName}</DialogTitle>
                <DialogDescription>
                  Appointment details and status
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium mb-2">Client Information</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center space-x-2">
                          <User className="w-4 h-4 text-muted-foreground" />
                          <span>{selectedAppointment.clientName}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Mail className="w-4 h-4 text-muted-foreground" />
                          <span>{selectedAppointment.clientEmail}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Phone className="w-4 h-4 text-muted-foreground" />
                          <span>{selectedAppointment.clientPhone}</span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-medium mb-2">Appointment Details</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center space-x-2">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          <span>{formatDate(selectedAppointment.date)}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Clock className="w-4 h-4 text-muted-foreground" />
                          <span>{formatTime(selectedAppointment.time)} ({selectedAppointment.duration} minutes)</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          {getLocationIcon(selectedAppointment.location)}
                          <span>{selectedAppointment.location}</span>
                        </div>
                        {selectedAppointment.locationDetails && (
                          <p className="text-muted-foreground ml-6">{selectedAppointment.locationDetails}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium mb-2">Status & Type</h4>
                      <div className="space-y-2">
                        <Badge className={getStatusColor(selectedAppointment.status)}>
                          {selectedAppointment.status}
                        </Badge>
                        <Badge variant="outline">{selectedAppointment.type}</Badge>
                      </div>
                    </div>

                    {selectedAppointment.propertyAddress && (
                      <div>
                        <h4 className="font-medium mb-2">Property</h4>
                        <p className="text-sm">{selectedAppointment.propertyAddress}</p>
                      </div>
                    )}

                    {selectedAppointment.documents && selectedAppointment.documents.length > 0 && (
                      <div>
                        <h4 className="font-medium mb-2">Required Documents</h4>
                        <div className="space-y-1">
                          {selectedAppointment.documents.map((doc, index) => (
                            <div key={index} className="flex items-center space-x-2 text-sm">
                              <FileText className="w-4 h-4 text-muted-foreground" />
                              <span>{doc}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {selectedAppointment.notes && (
                  <div>
                    <h4 className="font-medium mb-2">Notes</h4>
                    <p className="text-sm text-muted-foreground bg-muted p-3 rounded-lg">
                      {selectedAppointment.notes}
                    </p>
                  </div>
                )}

                <Separator />

                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    Created: {new Date(selectedAppointment.createdAt).toLocaleDateString()}
                    {selectedAppointment.updatedAt !== selectedAppointment.createdAt && (
                      <span> • Updated: {new Date(selectedAppointment.updatedAt).toLocaleDateString()}</span>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    {(selectedAppointment.status === 'Scheduled' || selectedAppointment.status === 'Confirmed') && (
                      <>
                        <Button
                          data-testid="appointments-detail-send-reminder-button"
                          size="sm"
                          variant="outline"
                          onClick={() => handleSendReminder(selectedAppointment.id)}
                        >
                          Send Reminder
                        </Button>
                        <Button
                          data-testid="appointments-detail-mark-complete-button"
                          size="sm"
                          onClick={() => handleUpdateStatus(selectedAppointment.id, 'Completed')}
                        >
                          Mark Complete
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
